import { MenuItem, Select, Typography } from '@mui/material';
import type { DynamicInvoiceMeasurementChartMeterUsage } from '@vy/api-definition';
import type { AggregationOperator, Currency, Period } from '@vy/types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from 'chart.js';
import React, { useMemo, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useChartUtils } from '../../../../hooks/use-chart-utils';
import {
  buildTiersChartData,
  getChartLabels,
  getChartValues,
} from '../dynamic-invoice-usage-line-item-chart-utils';
import { generateUsageLineItemBarChartOptions } from '../dynamic-invoice-view-page-consts/dynamic-invoice-usage-line-item-bar-chart-options';
import type { DynamicInvoiceUsageLineItemChartResolution } from '../dynamic-invoice-view-page-types/dynamic-invoice-usage-line-item-chart-resolution';
import {
  DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION,
  DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION_LABELS,
} from '../dynamic-invoice-view-page-types/dynamic-invoice-usage-line-item-chart-resolution';
import { checkIsTemporalAggregationOperator } from '@vy/services';
import { useTreatLastAsDaily } from '../../../../hooks/use-treat-last-as-daily';

ChartJS.register(PointElement, LineElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type UsageLineItemBarChartProps = {
  datasetLabel: string;
  meterUsageData: DynamicInvoiceMeasurementChartMeterUsage;
  period: Period;
  aggregationOperator: AggregationOperator;
  meterName?: string;
  meterUnits?: string;
  currency?: Currency;
};

export const UsageLineItemBarChart: React.FC<UsageLineItemBarChartProps> = ({
  datasetLabel,
  meterUsageData,
  period,
  aggregationOperator,
  meterName,
  meterUnits,
  currency,
}) => {
  const [dateResolution, setDateResolution] = useState<DynamicInvoiceUsageLineItemChartResolution>(
    DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.DAY,
  );
  const treatLastAsDaily = useTreatLastAsDaily();
  const { shouldShowLineChart, filterPastChartData } = useChartUtils(aggregationOperator, treatLastAsDaily);
  const isLineChart = shouldShowLineChart;

  const chartLabels = useMemo(() => getChartLabels(period, dateResolution), [dateResolution, period]);

  const chartValues = useMemo(
    () => getChartValues(meterUsageData, period, dateResolution, aggregationOperator, treatLastAsDaily),
    [dateResolution, meterUsageData, period, aggregationOperator, treatLastAsDaily],
  );

  const data = buildTiersChartData(chartLabels, chartValues, datasetLabel);
  const filteredData = filterPastChartData(chartLabels, data);
  const usageLineItemBarChartOptions = generateUsageLineItemBarChartOptions({
    unitCategory: meterUnits,
    currency,
    datasetLabelsLength: chartLabels.length,
  });

  return (
    <div className="mb-1 h-[358px] w-full">
      <div className="mb-5 w-full flex justify-between items-center">
        <div>
          <Typography
            variant="body1"
            sx={{
              fontSize: "16px",
              color: "#0d1b27de"
            }}>
            {meterName}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "14px",
              color: "#1b3d5599"
            }}>
            Sum up by ‘{aggregationOperator}’
          </Typography>
        </div>
        <div>
          <Select
            sx={{
              width: '10vw',
              height: '40px',
              backgroundColor: '#0D1B270A',
              boxShadow: 'none',
              '.MuiOutlinedInput-notchedOutline': { border: 0 },
            }}
            value={dateResolution}
            onChange={(event) =>
              setDateResolution(event.target.value as DynamicInvoiceUsageLineItemChartResolution)
            }
            disabled={checkIsTemporalAggregationOperator(aggregationOperator, treatLastAsDaily)}
          >
            {Object.entries(DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION_LABELS).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <div className="mb-1 h-[300px] w-full">
        {isLineChart ? (
          <Line data={filteredData} options={usageLineItemBarChartOptions} />
        ) : (
          <Bar data={data} options={usageLineItemBarChartOptions} />
        )}
      </div>
    </div>
  );
};
